import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Map, Radio, ScrollText } from 'lucide-react';
import { rerrApi } from '@/services/api.js';
import { useAuth } from '@/context/AuthContext.jsx';
import { Card, CardHeader, StatCard, QuickAction, EmptyState, ErrorPanel } from '@/components/common/Card.jsx';
import StatusBadge from '@/components/common/StatusBadge.jsx';
import { fmtRelative, fmtPriorityIndex } from '@/utils/format.js';

export default function AgencyHome() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const dispatchQuery = useQuery({
    queryKey: ['rerr', 'dispatch', 'open'],
    queryFn:  () => rerrApi.getDispatch({ status: 'open', per_page: 6 }),
    refetchInterval: 15000,
    retry:    1,
  });

  const boloQuery = useQuery({
    queryKey: ['rerr', 'bolos', 'active'],
    queryFn:  () => rerrApi.listBolos({ status: 'active', per_page: 6 }),
    retry:    1,
  });

  const tasks = dispatchQuery.data?.data ?? [];
  const bolos = boloQuery.data?.data ?? [];

  const openCount     = tasks.filter(entry => !['closed','failed'].includes(entry.status)).length;
  const protectedFlag = tasks.filter(entry => entry.protected_person_flag).length;

  return (
    <>
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('home.agencyHome.agencyDashboard')}</h1>
          <p className="mt-1.5 text-sm text-slate-600">
            {user?.agency_id ? `Agency ${user.agency_id}` : 'Operational overview'} {t('home.agencyHome.liveDispatchAndBoloSnapshot')}</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <StatCard label={t('home.agencyHome.openDispatch')}     value={dispatchQuery.isLoading ? '…' : openCount}      hint={t('home.agencyHome.tasksNotYetClosed')}        accent="rose" />
        <StatCard label="Protected-person"  value={dispatchQuery.isLoading ? '…' : protectedFlag}  hint={t('home.agencyHome.usssFastPathCandidates')}   accent="amber" />
        <StatCard label={t('home.agencyHome.activeBolo')}       value={boloQuery.isLoading ? '…' : bolos.length}       hint={t('home.agencyHome.currentlyPublished')}         accent="sky" />
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-10">
        <QuickAction to="/agency/dispatch" title={t('home.agencyHome.dispatchBoard')} description={t('home.agencyHome.liveMapQueueWithUnitAssignment')} icon={Map} />
        <QuickAction to="/agency/bolo"     title={t('home.agencyHome.boloManager')}   description={t('home.agencyHome.publishExpireOrRevokeBoloRecords')}     icon={Radio} />
        <QuickAction to="/agency/audit"    title={t('home.agencyHome.auditViewer')}   description={t('home.agencyHome.browseSignedIntegrityChainRecordsFor')}                  icon={ScrollText} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('home.agencyHome.openDispatchTasks')} description={t('home.agencyHome.mostRecentTasksRoutedToYour')} />
          <div className="mt-5">
            {dispatchQuery.isLoading ? (
              <SkeletonList rows={5} />
            ) : dispatchQuery.isError ? (
              <ErrorPanel error={dispatchQuery.error} onRetry={dispatchQuery.refetch} />
            ) : tasks.length === 0 ? (
              <EmptyState title={t('home.agencyHome.noOpenDispatchTasks')} description={t('home.agencyHome.whenNewReportsRouteToYour')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {tasks.map(entry => (
                  <li key={entry.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {entry.event_class?.replace(/_/g, ' ') || 'Dispatch task'} · <span className="text-slate-500">{entry.trace_id || entry.report_id?.slice(0, 8)}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        PI {fmtPriorityIndex(entry.priority_index)} · {fmtRelative(entry.created_at)}
                      </div>
                    </div>
                    <StatusBadge status={entry.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title={t('home.agencyHome.activeBolos')} description={t('home.agencyHome.currentlyPublishedAgencyLookoutRecords')} />
          <div className="mt-5">
            {boloQuery.isLoading ? (
              <SkeletonList rows={5} />
            ) : boloQuery.isError ? (
              <ErrorPanel error={boloQuery.error} onRetry={boloQuery.refetch} />
            ) : bolos.length === 0 ? (
              <EmptyState title={t('home.agencyHome.noActiveBolos')} description={t('home.agencyHome.publishOneFromTheBoloManager')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {bolos.map(b => (
                  <li key={b.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">{b.subject_summary || b.title || 'BOLO'}</div>
                      <div className="text-xs text-slate-500">{fmtRelative(b.published_at || b.created_at)}</div>
                    </div>
                    <StatusBadge status={b.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

function SkeletonList({ rows = 3 }) {
  return (
    <ul className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 py-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/2 rounded-full bg-slate-200 animate-pulse" />
            <div className="h-2.5 w-1/3 rounded-full bg-slate-100 animate-pulse" />
          </div>
          <div className="h-5 w-16 rounded-full bg-slate-100 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}
